import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Badge, Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import {
    loadScreenings, loadTickettypes, loadEmailLayouts,
    resetTickets, loadTickets, updateTicketsFilters,
    loadLogs, setMessagesRecipients
} from '../../../redux';
import { Loader, CopyButton, PageTitle, Table, EventTitle, TableButtons, WalletsManager } from '../../../components';
import { Filters, Utils } from '../../../services';
import { Tickettype, Log } from '../../../models';
import TicketsFilters from './TicketFilters/TicketsFilters';
import _ from 'lodash';

import 'react-table-6/react-table.css'
import './TicketsList.css';

const TicketsList = ({ }) => {
    const dispatch    = useDispatch();
    const history     = useHistory();
    const { t, i18n } = useTranslation();

    const [selection, setSelection]                     = useState([]);
    const [tableFilters, setTableFilters]               = useState([]);
    const [walletsManagerIsVisible, showWalletsManager] = useState(false);

    const { setting }                     = useSelector(state => state.settings);
    const { tickets, isLoading, filters } = useSelector(state => state.tickets)
    const emails                          = useSelector(state => state.emailLayouts.layouts)
    const { logs }                        = useSelector(state => state.logs)
    const { screenings }                  = useSelector(state => state.screenings)
    const { tickettypes }                 = useSelector(state => state.tickettypes)
    const { isMobile }                    = useSelector(state => state.ui);

    moment.locale(i18n.language);

    const importLogs = useMemo(() => logs?.filter(log => (
        log.type === Log.TICKETS_IMPORT && log.inserted?.length > 0
    )), [logs]);

    const preparedTickets = useMemo(() => {
        return tickets?.map(ticket => {
            const { firstname, lastname, email } = (ticket.contact || {});
            const prepared = {
                _id: ticket._id,
                status: ticket.status,
                contact: { firstname, lastname, email },
                vdr_auth_serial: ticket.vdr_auth_serial,
                vdr_auth_key: ticket.vdr_auth_key,
                nbookings: ticket.bookings?.length || 0,
                created_at: ticket.created_at
            }
            prepared.owner = [ firstname, lastname, email].join(' ');

            const tickettype = tickettypes?.find(t => t._id === ticket.type?._id);
            prepared.type_name = tickettype ?
                Utils.localized_or_fallback(tickettype.name, i18n.lang) : '?';

            prepared.activated_pricing_name = ticket.activated_pricing ?
                Utils.localized_or_fallback(ticket.activated_pricing.name, i18n.lang) : '-';

            return prepared;
        });
    }, [ tickets, i18n.lang, tickettypes ]);

    const isSelected = React.useCallback(_id => selection.includes(_id), [ selection ]);
    const onChangedCheckbox = _id => {
        if (isSelected(_id))
            setSelection(selection.filter(i => i !== _id));
        else
            setSelection([...selection, _id]);
    };
    const selectAll = React.useCallback(all => {
        setSelection(all ?
            tickets?.map(t => t._id) :
            []
        );
    }, [tickets]);
    const allSelected = useMemo(() => {
        if (!tickets?.length || !selection?.length)
            return false;

        if (tickets.length !== selection?.length)
            return false;

        return tickets.reduce(
            (allSelected, ticket) => allSelected && selection.includes(ticket._id),
            true
        );
    }, [tickets, selection]);

    const submit = (values) => {
        const payload = _.cloneDeep(values || {});
        payload.without_one_time_pass = !payload?.types?.includes(Tickettype.ONE_TIME_PASS);
        dispatch(updateTicketsFilters(payload));
    }

    const sendBatchEmail = () => {
        const recipients = selection?.map(_id => {
            const ticket = tickets?.find(t => t._id === _id);
            if (!ticket?.contact.email)
                return null;

            return {
                to: {
                    name: [
                        ticket.contact?.firstname,
                        ticket.contact?.lastname
                    ].filter(Boolean).join(' ') || t.contact?.email,
                    address: ticket.contact.email
                },
                context: {
                    ticket_id: ticket._id
                }
            };
        }).filter(Boolean);
        dispatch(setMessagesRecipients(recipients));
        history.push('/messages');
    }

    const creditWallets = () => {
        showWalletsManager(true);
    };

    useEffect(() => {
        dispatch(loadScreenings({
            start_at_gte: moment().toDate(),
        }));
        dispatch(loadTickettypes());
        dispatch(loadEmailLayouts());
        dispatch(loadLogs({
            types: Log.TICKETS_IMPORT,
            sort: 'created_at',
            dir: 'desc',
            limit: 100,
            fields: ['_id', 'created_at', 'type', 'source', 'nbDeleted', 'inserted._id', 'activated._id']
        }));
    }, [dispatch])

    const filtersSet = () => {
        return (
            filters.search ||
            filters.without_one_time_pass ||
            filters.types?.length ||
            filters.having_not_booked ||
            filters.having_booked ||
            filters.having_booked_screening_id ||
            filters.received_email ||
            filters.not_received_email
        );
    }

    useEffect(() => {
        if (filtersSet())
            dispatch(loadTickets(filters));
        else
            dispatch(resetTickets());
    }, [dispatch, filters])

    const title =
        <span>{t("common.ticket", {count: 42})}</span>

    const columns = [{
        Header: cell => tableFilters?.length > 0 ? null : (
            <input
                className="checkbox"
                type="checkbox"
                checked={!!allSelected}
                onChange={() => selectAll(!allSelected)}
                value={cell.value}
            />
        ),
        filterable: false,
        sortable: false,
        accessor: '_id',
        maxWidth: 90,
        className: "checkbox-cell",
        Cell: cell => (
            <input
                className="checkbox"
                type="checkbox"
                checked={isSelected(cell.value)}
                onChange={() => onChangedCheckbox(cell.value)} value={cell.value}
            />
        )
    }, {
        Header: t("tickets.columns._id"),
        filterAll: true,
        accessor: '_id',
        filterMethod: Filters.textFilterMethod(['_id', 'vdr_auth_serial']),
        Filter: Filters.textFilter,
        Cell: cell => (
            <div className="d-flex gap-1">
                <CopyButton
                    badge
                    size="xs"
                    color="info"
                    title={ 'ID: ' + cell.value.substring(0, 8) + '...' }
                    textToCopy={ cell.value }
                    className="inline"
                />
                {cell.original.vdr_auth_serial && (
                    <CopyButton
                        badge
                        size="xs"
                        color="dark"
                        title={ 'TicketID: ' + cell.original.vdr_auth_serial }
                        textToCopy={ cell.original.vdr_auth_serial + '-' + cell.original.vdr_auth_key }
                        className="inline"
                    />
                )}
            </div>
        )
    }, {
        Header: t("tickets.columns.owner"),
        filterAll: true,
        accessor: 'owner',
        filterMethod: Filters.textFilterMethod('owner'),
        Filter: Filters.textFilter,
        Cell: cell => {
            const { contact } = cell.original;
            return (
            <>
                <b>
                    {[contact.lastname, contact.firstname].filter(Boolean).join(' ')}
                </b>
                <div>
                    <small>{ contact.email }</small>
                </div>
            </>
            )
        }
    }, {
        Header: t("tickets.columns.type"),
        filterable: false,
        accessor: 'type_name',
    }, {
        Header: t("tickets.columns.activated_pricing"),
        filterAll: true,
        accessor: 'activated_pricing_name',
        filterMethod: Filters.textFilterMethod(['activated_pricing_name']),
        Filter: Filters.textFilter,
    }, {
        Header: t("tickets.columns.nbookings"),
        filterAll: true,
        accessor: 'nbookings',
        filterMethod: Filters.textFilterMethod(['nbookings']),
        Filter: Filters.textFilter,
        className: 'text-center'
    }, {
        Header: t("tickets.columns.created_at"),
        filterAll: true,
        accessor: 'created_at',
        Filter: Filters.dateFilter(false),
        filterMethod: Filters.sameDayDateFilterMethod('created_at'),
        className: 'text-center',
        Cell: cell => moment(cell.value).format('LLL')
    }, {
        Header: t("tickets.columns.status"),
        accessor: 'status',
        filterable: false,
        className: 'text-center',
        Cell: (cell, row) => t(`tickets.status.${cell.value}`)
    }, {
        Header: t("common.actions"),
        accessor: '_id',
        className: "actions-cell",
        maxWidth: 90,
        filterable: false,
        sortable: false,
        Cell: cell =>
        <Button
            color="primary"
            href={`${setting?.eshop?.uri}/ticket/view/?uuid=${cell.value}`}
            target="_blank"
        >
            <i className="fa fa-external-link"></i>
        </Button>
    }];

    const actions = null;

    return (
        <div className="TicketsList">
            <PageTitle icon="th" title={title} actions={actions} />
            <TicketsFilters
                screenings={screenings}
                tickettypes={tickettypes}
                importLogs={importLogs}
                emails={emails}
                onSubmit={submit}
                isLoading={isLoading}
            />
            {isLoading && <Loader /> }
            {!isLoading && filtersSet() && preparedTickets?.length > 0 && (
                <h4 className="text-right">
                    <Badge color="dark">
                        <i className="fa fa-search" />&nbsp;
                        {t('tickets.nb_showed', {count: preparedTickets.length})}
                    </Badge>
                </h4>
            )}
            {!isLoading && filtersSet() && !preparedTickets?.length && (
                <h4 className="text-right">
                    <Badge color="dark">
                        <i className="fa fa-frown-o" />&nbsp;
                        {t('tickets.nothing_found')}
                    </Badge>
                </h4>
            )}
            {!isLoading && (preparedTickets?.length > 0 && columns?.length > 0) &&
            <>
                {selection?.length > 0 && (
                    <div className="border rounded d-flex gap-2 p-1 mb-1 justify-between">
                        <div>
                            <Button onClick={ sendBatchEmail } color="primary" className="mr-2">
                                <i className="fa fa-envelope" />&nbsp;
                                { t('tickets.batch.send_email') }
                            </Button>
                            <Button onClick={ creditWallets } color="primary">
                                <i className="fa fa-money" />&nbsp;
                                { t('tickets.batch.credit_wallet') }
                            </Button>
                        </div>
                        <h4>
                            <Badge color="info" className="mb-1 mr-1">
                                <i className="fa fa-check" />&nbsp;
                                {t('tickets.nb_selected', {count: selection.length})}
                            </Badge>
                        </h4>
                    </div>
                )}
                <Table
                    id="ticketsTable"
                    data={preparedTickets}
                    columns={columns}
                    columnsSortOrder={["firstname"]}
                    onFilter={setTableFilters}
                />
            </>
            }
            {walletsManagerIsVisible && (
                <WalletsManager ticketIds={selection} show={walletsManagerIsVisible} onClose={() => showWalletsManager(false)} />
            )}
        </div>
    );
}

export default TicketsList
